/* eslint-disable @typescript-eslint/no-unused-vars */
import type { FavoriteAPIInterface } from './api-switcher'
import { client } from './client'
import { responseDataConverter, apiPhotoToMyPhoto } from './converters'
import { special_favorite_id } from '../utils/config'

export const FavoriteAPI: FavoriteAPIInterface = {
  // List all favorite collections
  listFavorites: (signal) =>
    // TODO: No direct API endpoint for favorite collections
    // For now, return a single default favorite collection
    Promise.resolve({
      data: [{
        id: special_favorite_id,
        name: 'Default',
        thumbnail_url: '',
        last_append: new Date()
      }],
      response: new Response()
    }),

  // Get a specific favorite collection with photos
  getFavorite: (id, signal) => {
    if (id !== special_favorite_id) {
      return Promise.resolve({
        data: undefined,
        error: { code: 404, message: 'Favorite collection not found' },
        response: new Response('', { status: 404 })
      })
    }

    // Get all favorite photos
    return responseDataConverter(
      client.GET('/favorite', {
        params: {
          query: {
            direction: 'past',
            limit: 100 // Get more photos for the collection
          }
        },
        signal
      }),
      (data) => ({
        id: special_favorite_id,
        name: 'Default',
        thumbnail_url: data.photos[0]?.urls?.thumbnail || '',
        last_append: data.photos[0] ? new Date(data.photos[0].added_at) : new Date(),
        photos: data.photos.map(photo => ({
          ...apiPhotoToMyPhoto(photo),
          favorite: true
        }))
      })
    )
  },

  // Create a new favorite collection - not supported, only default exists
  createFavorite: (name, photo_ids) =>
    Promise.resolve({
      error: { code: 0, message: 'Multiple favorite collections not supported' },
      response: new Response()
    }),

  // Update favorite collection - not supported
  updateFavorite: (id, data) =>
    Promise.resolve({
      error: { code: 0, message: 'Favorite collection update not supported' },
      response: new Response()
    }),

  // Delete favorite collection - not supported
  deleteFavorite: (id) =>
    Promise.resolve({
      error: { code: 0, message: 'Cannot delete default favorite collection' },
      response: new Response()
    }),

  // Add photos to favorites
  appendPhotosToFavorite: async (id, photo_ids) => {
    if (id !== special_favorite_id) {
      return {
        error: { code: 404, message: 'Favorite collection not found' },
        response: new Response('', { status: 404 })
      }
    }

    // Add each photo to favorites
    const results = await Promise.all(
      photo_ids.map(photo_id =>
        client.POST('/favorite/{photoId}', {
          params: { path: { photoId: photo_id } }
        })
      )
    )

    // Check if any failed
    const failed = results.find(r => r.error)
    if (failed) {
      return {
        error: failed.error,
        response: failed.response
      }
    }

    return {
      response: new Response()
    }
  },

  // Remove photos from all favorite collections
  removePhotosFromAllFavorites: async (photo_ids) => {
    // Remove each photo from favorites
    const results = await Promise.all(
      photo_ids.map(photo_id =>
        client.DELETE('/favorite/{photoId}', {
          params: { path: { photoId: photo_id } }
        })
      )
    )

    // Check if any failed
    const failed = results.find(r => r.error)
    if (failed) {
      return {
        error: failed.error,
        response: failed.response
      }
    }

    return {
      response: new Response()
    }
  },

  // List all photo dates in a favorite collection
  listAllPhotosDateFromFavorite: (id, desc, signal) => {
    if (id !== special_favorite_id) {
      return Promise.resolve({
        data: undefined,
        error: { code: 404, message: 'Favorite collection not found' },
        response: new Response('', { status: 404 })
      })
    }

    // Get all favorite photos and extract dates
    return responseDataConverter(
      client.GET('/favorite', {
        params: {
          query: {
            direction: desc ? 'past' : 'future',
            limit: 100
          }
        },
        signal
      }),
      (data) => data.photos.map(photo => new Date(photo.added_at))
    )
  },

  // List photos from a favorite collection with pagination
  listPhotosFromFavorite: (id, desc, offset_date, signal) => {
    if (id !== special_favorite_id) {
      return Promise.resolve({
        data: undefined,
        error: { code: 404, message: 'Favorite collection not found' },
        response: new Response('', { status: 404 })
      })
    }

    return responseDataConverter(
      client.GET('/favorite', {
        params: {
          query: {
            direction: desc ? 'past' : 'future',
            startTimestamp: offset_date?.toISOString()
          }
        },
        signal
      }),
      (data) => data.photos.map(photo => ({
        ...apiPhotoToMyPhoto(photo),
        favorite: true
      }))
    )
  },

  // Get a specific photo from favorites
  getPhotoFromFavorite: async (favorite_id, photo_id, signal) => {
    if (favorite_id !== special_favorite_id) {
      return {
        error: { code: 404, message: 'Favorite collection not found' },
        response: new Response('', { status: 404 })
      }
    }

    // Get the photo
    const photoResponse = await client.GET('/my/{PhotoId}', {
      params: { path: { PhotoId: photo_id } },
      signal
    })

    if (photoResponse.error) {
      return {
        error: photoResponse.error,
        response: photoResponse.response
      }
    }

    // Check if it's actually in favorites by trying to get favorites
    const favoritesResponse = await client.GET('/favorite', {
      params: {
        query: {
          direction: 'past',
          limit: 100
        }
      },
      signal
    })

    const isInFavorites = favoritesResponse.data?.photos.some(
      p => p.photo_id === photo_id
    ) || false

    if (!isInFavorites) {
      return {
        error: { code: 404, message: 'Photo not in favorites' },
        response: new Response('', { status: 404 })
      }
    }

    return {
      data: {
        ...apiPhotoToMyPhoto(photoResponse.data),
        favorite: true
      },
      response: new Response()
    }
  },

  // Add/remove reaction to a photo in favorites
  reactionToFavoritePhoto: (favorite_id, photo_id, reaction, add) => {
    if (favorite_id !== special_favorite_id) {
      return Promise.resolve({
        data: undefined,
        error: { code: 404, message: 'Favorite collection not found' },
        response: new Response('', { status: 404 })
      })
    }

    return responseDataConverter(
      client.POST('/my/{PhotoId}/reaction', {
        params: {
          path: { PhotoId: photo_id }
        },
        body: {
          action: add ? 'add' : 'remove',
          reaction: reaction
        },
      }),
      (photo) => ({
        ...apiPhotoToMyPhoto(photo),
        favorite: true
      })
    )
  },

  // Tag users in a photo in favorites
  tagsToFavoritePhoto: (favorite_id, photo_id, in_user_ids) => {
    if (favorite_id !== special_favorite_id) {
      return Promise.resolve({
        data: undefined,
        error: { code: 404, message: 'Favorite collection not found' },
        response: new Response('', { status: 404 })
      })
    }

    return responseDataConverter(
      client.POST('/my/{PhotoId}/tag', {
        params: {
          path: { PhotoId: photo_id },
        },
        body: { in_user_ids },
      }),
      (photo) => ({
        ...apiPhotoToMyPhoto(photo),
        favorite: true
      })
    )
  },

  reportFavoritePhoto: (favorite_id, photo_id) => {
    console.log('reportFavoritePhoto (not implemented)', favorite_id, photo_id)
    return Promise.resolve({
      error: {code: 501, message: 'Not Implemented'},
      response: new Response('', {status: 501})
    })
  }
}